import {
	ReactElement,
	useCallback,
	useContext,
	useMemo,
	useState
} from "react";
import { LiveTileContent } from "../LiveTileContent";
import { TileContext } from "../TileContext";
import {
	BusyIndicator,
	IFrame,
	IFramePermission,
	PopoutableContent,
	Portal
} from "client/Components";
import { hasStackingContextIssues } from "client_legacy/ndaw/mobile";
import { generateEmbedUrl } from "client/Misc/youTube";
import styles from "./YouTubeLiveTileContent.module.less";
import { useAddAnalyticsAppEvent } from "client/Features/Analytics/useAddAnalyticsAppEvent";
import { AppType } from "client/Api/Apps/common";
import { AppTypeSpecificTileSettings } from "../../RenderState/common";
import { Action, ActionKind } from "client_legacy/ndaw/actions";
import { localize } from "client_legacy/ndaw/culture";
import { LiveTileOverlayState, useLiveTileOverlayState } from "../useLiveTileOverlayState";
import { Coordinate } from "client/Misc/commonTypes";
import { TileStatus } from "../TileStatus";

export type YouTubeTileSettings = AppTypeSpecificTileSettings<AppType.YouTube>;

export type YouTubeLiveTileContentProps = {
	settings: YouTubeTileSettings;
	onOpenTileMenu: (coordinate: Coordinate) => void;
};

export const YouTubeLiveTileContent = (props: YouTubeLiveTileContentProps): ReactElement => {
	const {
		settings,
		onOpenTileMenu
	} = props;

	const {
		playlistId,
		videoId,
		hideHeader
	} = settings;

	const [isPoppedOut, setIsPoppedOut] = useState<boolean>(false);
	const [isLoading, setIsLoading] = useState<boolean>(true);

	const {
		name,
		icon,
		environmentAppId,
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	} = useContext(TileContext);

	const {
		addEvent: addAnalyticsEvent
	} = useAddAnalyticsAppEvent(environmentAppId);

	const overlayState: LiveTileOverlayState = useLiveTileOverlayState({
		interactionState,
		dimension,
		appIsLicensed,
		appIsNotAccessible
	});

	const popoutHandler = useCallback(
		(): void => {
			setIsPoppedOut(true);
			addAnalyticsEvent();
		},
		[addAnalyticsEvent]
	);

	const useSeparateDomForPopout: boolean = useMemo(
		() => hasStackingContextIssues(),
		[]
	);

	const embedUrl = useMemo(
		(): string | undefined => {
			if (!(videoId || playlistId)) {
				return undefined;
			}

			return generateEmbedUrl({
				videoId,
				listId: playlistId!
			});
		},
		[videoId, playlistId]
	);

	const defaultActions = useMemo(
		(): readonly Action<ActionContext>[] => listDefaultActions({
			enlarge: () => popoutHandler()
		}),
		[popoutHandler]
	);

	const actionContext = useMemo(
		(): ActionContext => ({
			canBeEnlarged: Boolean(embedUrl) && !isLoading
		}),
		[embedUrl, isLoading]
	);

	return (
		<>
			<LiveTileContent
				name={name}
				icon={icon}
				overlay={overlayState}
				onOpenTileMenu={onOpenTileMenu}
				overlayStatusIcon={<TileStatus />}
				isPoppedOut={isPoppedOut}
			>
				{!hideHeader && (
					<LiveTileContent.Header
						icon={icon}
						actions={defaultActions}
						actionContext={actionContext}
					>
						<LiveTileContent.Title text={name} />
					</LiveTileContent.Header>
				)}

				<LiveTileContent.Content>
					<PopoutableContent
						isPoppedOut={!useSeparateDomForPopout && isPoppedOut}
						onPopin={() => setIsPoppedOut(false)}
						title={name}
					>
						{embedUrl && (
							<EmbedContent
								url={embedUrl}
								onLoad={() => setIsLoading(false)}
								isLoading={isLoading}
							 />
						)}

					</PopoutableContent>

				</LiveTileContent.Content>
			</LiveTileContent>

			{useSeparateDomForPopout && embedUrl && isPoppedOut && (
				<Portal container={document.body}>
					<PopoutableContent
						isPoppedOut
						onPopin={() => setIsPoppedOut(false)}
						title={name}
					>
						<EmbedContent url={embedUrl} isLoading={false} />
					</PopoutableContent>
				</Portal>
			)}
		</>
	);
};
YouTubeLiveTileContent.displayName = "YouTubeLiveTileContent";

type ActionContext = {
	canBeEnlarged: boolean;
};

type ActionHandlers = {
	enlarge: () => void;
};

function listDefaultActions(
	handlers: ActionHandlers
): readonly Action<ActionContext>[] {
	return [
		{
			kind: ActionKind.Regular,
			name: "enlarge",
			title: localize("_Enlarge"),
			icon: "arrow-up-right-from-square",
			canExecute: (x) => x.canBeEnlarged,
			execute: () => handlers.enlarge()
		}
	];
}

type EmbedContentProps = {
	url: string;
	isLoading: boolean;
	onLoad?: () => void;
};

const EmbedContent = (props: EmbedContentProps): ReactElement => {
	const {
		url,
		isLoading,
		onLoad
	} = props;

	return (
		<div className={styles.hostContainer}>
			{isLoading && <BusyIndicator />}

			<IFrame
				className="fill"
				frameBorder="0"
				allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
				allowFullScreen
				src={url}
				onLoad={onLoad}
				trackingId="youtube-tile-iframe"
				permissions={youtubeAppIframePermissions}
			/>
		</div>
	);
};
EmbedContent.displayName = "EmbedContent";

export const youtubeAppIframePermissions: readonly IFramePermission[] = [
	IFramePermission.Downloads,
	IFramePermission.Forms,
	IFramePermission.Modals,
	IFramePermission.OrientationLock,
	IFramePermission.PointerLock,
	IFramePermission.Popups,
	IFramePermission.EscapingPopups,
	IFramePermission.Presentation,
	IFramePermission.SameOrigin,
	IFramePermission.Scripts
];

